import { Box, Flex, Text } from "@chakra-ui/react";
import React from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import PayrollAuditChange from "./PayrollAuditChange";

type Props = {
  values: number[];
  /** The same measure in a compared batch, drawn beside each bar. */
  compareValues?: number[];
  compareLabel?: string;
  formatValue: (value: number) => string;
  /** Shorter labels for the axis ticks. Defaults to formatValue. */
  formatTick?: (value: number) => string;
  /** Bins never get narrower than this, e.g. 1 for counts. */
  minBinWidth?: number;
  hideOutliers: boolean;
};

type Bin = {
  from: number;
  to: number;
  center: number;
  count: number;
  compareCount: number;
  expected: number;
  compareExpected: number;
};

type Stats = {
  count: number;
  mean: number;
  median: number;
  stdDev: number;
  min: number;
  max: number;
};

// Blue and orange stay apart for color-blind readers; the compared batch is also drawn as outlines
// with a dashed curve, so the two never rely on color alone.
const CURRENT_COLOR = "#3182CE";
const CURRENT_CURVE_COLOR = "#2A4365";
const COMPARE_COLOR = "#DD6B20";
const COMPARE_CURVE_COLOR = "#9C4221";

/**
 * A histogram of the values with the normal curve of the same mean and standard deviation drawn
 * over it, both in counts per bin so they share one axis. A compared batch is drawn as outlines over
 * the same bins, with its own curve, so both shapes show where they overlap.
 */
export default function PayrollAuditDistributionChart(props: Props) {
  const { bins, stats, compareStats, hidden } = React.useMemo(
    () => buildHistogram(props.values, props.compareValues, props.hideOutliers, props.minBinWidth),
    [props.values, props.compareValues, props.hideOutliers, props.minBinWidth]
  );

  if (stats === null) {
    return (
      <Text color="gray.500" fontSize="sm" py={8} textAlign="center">
        Nothing to plot
      </Text>
    );
  }

  const hasComparison = compareStats !== null;
  const statLine = (label: string, pick: (s: Stats) => number, format: (v: number) => string) => (
    <Text>
      {label} {format(pick(stats))}
      {hasComparison ? (
        <PayrollAuditChange current={pick(stats)} previous={pick(compareStats)} />
      ) : null}
    </Text>
  );

  return (
    <Box>
      <Flex color="gray.600" flexWrap="wrap" fontSize="sm" gap={5} mb={2}>
        {statLine(
          "n =",
          (s) => s.count,
          (v) => v.toLocaleString()
        )}
        {statLine("Mean", (s) => s.mean, props.formatValue)}
        {statLine("Median", (s) => s.median, props.formatValue)}
        {statLine("Std dev", (s) => s.stdDev, props.formatValue)}
        <Text>
          Range {props.formatValue(stats.min)} – {props.formatValue(stats.max)}
        </Text>
        {hidden > 0 ? (
          <Text color="gray.500">
            {hidden.toLocaleString()} outliers outside the 1st–99th percentile not shown
          </Text>
        ) : null}
      </Flex>
      <ResponsiveContainer height={300} width="100%">
        <ComposedChart
          barCategoryGap={2}
          data={bins}
          margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
        >
          <CartesianGrid stroke="#EDF2F7" vertical={false} />
          <XAxis
            dataKey="center"
            fontSize={11}
            stroke="#A0AEC0"
            tickFormatter={(value: number) => (props.formatTick ?? props.formatValue)(value)}
            tickLine={false}
          />
          {/* A second, hidden axis on the same bins lets the compared outlines sit over the current bars. */}
          {hasComparison ? <XAxis hide dataKey="center" xAxisId="compare" /> : null}
          <YAxis allowDecimals={false} fontSize={11} stroke="#A0AEC0" tickLine={false} width={48} />
          <ChartTooltip
            content={({ active, payload }) => {
              const bin = payload?.[0]?.payload as Bin | undefined;
              if (active !== true || bin === undefined) {
                return null;
              }
              return (
                <Box bg="white" borderRadius="md" boxShadow="md" fontSize="sm" px={3} py={2}>
                  <Text fontWeight="semibold">
                    {props.formatValue(bin.from)} – {props.formatValue(bin.to)}
                  </Text>
                  <Flex alignItems="center" gap={2}>
                    <Box bg={CURRENT_COLOR} borderRadius="sm" h="10px" opacity={0.45} w="10px" />
                    <Text>
                      {bin.count.toLocaleString()} in this batch
                      <Text as="span" color="gray.500">
                        {" "}
                        ({bin.expected.toFixed(1)} if normal)
                      </Text>
                    </Text>
                  </Flex>
                  {hasComparison ? (
                    <Flex alignItems="center" gap={2}>
                      <Box
                        borderColor={COMPARE_COLOR}
                        borderRadius="sm"
                        borderWidth={2}
                        h="10px"
                        w="10px"
                      />
                      <Text>
                        {bin.compareCount.toLocaleString()} in{" "}
                        {props.compareLabel ?? "compared batch"}
                        <Text as="span" color="gray.500">
                          {" "}
                          ({bin.compareExpected.toFixed(1)} if normal)
                        </Text>
                      </Text>
                    </Flex>
                  ) : null}
                </Box>
              );
            }}
            cursor={{ fill: "rgba(160, 174, 192, 0.15)" }}
          />
          <Bar
            dataKey="count"
            fill={CURRENT_COLOR}
            fillOpacity={hasComparison ? 0.45 : 1}
            isAnimationActive={false}
            radius={[4, 4, 0, 0]}
          />
          {hasComparison ? (
            <Bar
              dataKey="compareCount"
              fill={COMPARE_COLOR}
              fillOpacity={0.08}
              isAnimationActive={false}
              radius={[4, 4, 0, 0]}
              stroke={COMPARE_COLOR}
              strokeWidth={2}
              xAxisId="compare"
            />
          ) : null}
          {hasComparison ? (
            <Line
              dataKey="compareExpected"
              dot={false}
              isAnimationActive={false}
              stroke={COMPARE_CURVE_COLOR}
              strokeDasharray="6 4"
              strokeWidth={2}
              type="monotone"
            />
          ) : null}
          <Line
            dataKey="expected"
            dot={false}
            isAnimationActive={false}
            stroke={CURRENT_CURVE_COLOR}
            strokeDasharray={hasComparison ? undefined : "4 3"}
            strokeWidth={2}
            type="monotone"
          />
        </ComposedChart>
      </ResponsiveContainer>
      <Flex color="gray.600" flexWrap="wrap" fontSize="xs" gap={4} justifyContent="flex-end">
        <Flex alignItems="center" gap={1}>
          <Box
            bg={CURRENT_COLOR}
            borderRadius="sm"
            h="10px"
            opacity={hasComparison ? 0.45 : 1}
            w="10px"
          />
          This batch
        </Flex>
        <Flex alignItems="center" gap={1}>
          <Box
            borderColor={CURRENT_CURVE_COLOR}
            borderStyle={hasComparison ? "solid" : "dashed"}
            borderTopWidth={2}
            w="16px"
          />
          Normal curve (this batch)
        </Flex>
        {hasComparison ? (
          <>
            <Flex alignItems="center" gap={1}>
              <Box
                bg="rgba(221, 107, 32, 0.08)"
                borderColor={COMPARE_COLOR}
                borderRadius="sm"
                borderWidth={2}
                h="10px"
                w="10px"
              />
              {props.compareLabel ?? "Compared batch"}
            </Flex>
            <Flex alignItems="center" gap={1}>
              <Box
                borderColor={COMPARE_CURVE_COLOR}
                borderStyle="dashed"
                borderTopWidth={2}
                w="16px"
              />
              Normal curve ({props.compareLabel ?? "compared batch"})
            </Flex>
          </>
        ) : null}
      </Flex>
    </Box>
  );
}

function quantile(sorted: number[], q: number) {
  const position = (sorted.length - 1) * q;
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower);
}

/** The normal probability density at x for the mean and standard deviation of the stats. */
function density(stats: Stats, x: number) {
  return stats.stdDev === 0
    ? 0
    : Math.exp(-((x - stats.mean) ** 2) / (2 * stats.stdDev ** 2)) /
        (stats.stdDev * Math.sqrt(2 * Math.PI));
}

function getStats(sorted: number[]): Stats | null {
  const count = sorted.length;
  if (count === 0) {
    return null;
  }
  const mean = sorted.reduce((sum, value) => sum + value, 0) / count;
  const stdDev = Math.sqrt(sorted.reduce((sum, value) => sum + (value - mean) ** 2, 0) / count);
  return {
    count,
    mean,
    median: quantile(sorted, 0.5),
    stdDev,
    min: sorted[0],
    max: sorted[count - 1],
  };
}

function buildHistogram(
  values: number[],
  compareValues: number[] | undefined,
  hideOutliers: boolean,
  minBinWidth?: number
) {
  const sorted = [...values].sort((a, b) => a - b);
  const compareSorted =
    compareValues === undefined ? null : [...compareValues].sort((a, b) => a - b);
  const stats = getStats(sorted);
  const compareStats = compareSorted === null ? null : getStats(compareSorted);

  if (stats === null) {
    return { bins: [], stats: null, compareStats: null, hidden: 0 };
  }

  // Both batches share the bins, so the range covers both.
  const all = compareSorted === null ? sorted : [...sorted, ...compareSorted].sort((a, b) => a - b);
  const clip = hideOutliers && all.length >= 100;
  const lowerBound = clip ? quantile(all, 0.01) : all[0];
  const upperBound = clip ? quantile(all, 0.99) : all[all.length - 1];
  const isShown = (value: number) => value >= lowerBound && value <= upperBound;
  const shown = sorted.filter(isShown);
  const compareShown = (compareSorted ?? []).filter(isShown);

  const binCount = Math.min(40, Math.max(5, Math.ceil(Math.sqrt(shown.length))));
  const span = upperBound - lowerBound;
  // Whole-number data (minBinWidth set) gets whole-number bins centred on the values.
  const width =
    minBinWidth === undefined
      ? Math.max(span / binCount, span === 0 ? 1 : 0)
      : Math.max(minBinWidth, Math.ceil(span / binCount));
  const start = minBinWidth === undefined ? lowerBound : lowerBound - width / 2;
  const actualBinCount = Math.max(1, Math.ceil((upperBound - start) / width + 1e-9));

  const bins: Bin[] = Array.from({ length: actualBinCount }, (_, index) => {
    const from = start + index * width;
    const center = from + width / 2;
    return {
      from,
      to: from + width,
      center,
      count: 0,
      compareCount: 0,
      expected: density(stats, center) * width * shown.length,
      compareExpected:
        compareStats === null ? 0 : density(compareStats, center) * width * compareShown.length,
    };
  });

  const binIndex = (value: number) =>
    Math.min(actualBinCount - 1, Math.floor((value - start) / width));

  for (const value of shown) {
    bins[binIndex(value)].count += 1;
  }
  for (const value of compareShown) {
    bins[binIndex(value)].compareCount += 1;
  }

  return { bins, stats, compareStats, hidden: sorted.length - shown.length };
}
